import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { createTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { apiService } from '../../../services/api';
import { useAppDispatch, useAppSelector, useToast } from '../../../hooks';
import { bindActionCreators } from '@reduxjs/toolkit';
import { userActions } from '../../../store/user';
import clsx from 'clsx';
import { ThemeProvider } from '@emotion/react';
import { theme as appTheme } from '../../../theme';
import { ROUTES } from '../../../utils/constants';
import { useNavigate } from 'react-router-dom';

const theme = createTheme({
  ...appTheme,
  components: {
    ...appTheme.components,
    MuiButton: {
      variants: [
        {
          props: { color: 'primary' },
          style: {
            backgroundColor: '#FFFFFF',
          },
        },
        {
          props: { color: 'secondary' },
          style: {
            backgroundColor: '#D3D3D3',
          },
        },
      ],
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '18px',
          backgroundColor: '#FFFFFF',
          borderRadius: '9999px',
          padding: '0.5rem',
          height: '3rem',
          boxSizing: 'border-box',
          whiteSpace: 'nowrap',
          color: '#000',
          '&:hover': {
            backgroundColor: appTheme.palette.primary.main,
          },
        },
      },
    },
  },
});

export type NavUserProps = {
  color?: 'primary' | 'secondary';
};

export function NavUser({ color = 'primary' }: NavUserProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();
  const { logout } = bindActionCreators(userActions, dispatch);
  const user = useAppSelector((state) => state.user);
  const toast = useToast();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const navigate = useNavigate();
  const handleProfile = () => {
    handleClose();
    navigate(ROUTES.users.me);
  };

  const handleLogout = async () => {
    if (!user) return;

    setIsLoading(true);
    handleClose();
    await apiService
      .logout(user.id)
      .then(() => {
        toast.success('Usuário deslogado com sucesso!');
        logout();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <ThemeProvider theme={theme}>
      <Button
        color={color}
        className={clsx('w-[200px] justify-between')}
        disabled={isLoading}
        id="nav-user-button"
        aria-controls={open ? 'nav-user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        endIcon={
          <KeyboardArrowDownIcon
            className={clsx('mr-2', open ? 'rotate-180' : 'rotate-0')}
          />
        }
      >
        <span className="ml-2 truncate">{user?.displayName}</span>
      </Button>
      <Menu
        id="nav-user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            width: '200px',
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        MenuListProps={{
          'aria-labelledby': 'nav-user-button',
        }}
      >
        <MenuItem onClick={handleProfile}>Perfil</MenuItem>
        <MenuItem onClick={handleLogout}>Sair</MenuItem>
      </Menu>
    </ThemeProvider>
  );
}
